import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import ButtonV2 from "../../../../components/ui/button/v2";
import { Account } from "../../../../api/accounts";
import AccountRow from "./AccountRow";
import CreateCaseAddAccountsModal from "./CreateCaseAddAccountsModal";
import { SectionAccordion, ViewMoreButton } from "./SectionShell";
import { SectionIssue } from "./derive";

export const INLINE_ROW_LIMIT = 2;

const emptyAccount = (): Account => ({
  account_number: uuidv4(),
  ext_account_number: "",
  account_name: "",
  account_type: "",
  account_category: "",
});

export interface AccountsSectionProps {
  accounts: Account[];
  onChange: (next: Account[]) => void;
  showErrors: boolean;
  issue: SectionIssue;
  focus: { key: string; seq: number };
}

const AccountsSection: React.FC<AccountsSectionProps> = ({
  accounts,
  onChange,
  showErrors,
  issue,
  focus,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const addEnabled = issue.incompleteRows === 0;

  useEffect(() => {
    if (focus.key !== "accounts") return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (issue.hiddenInvalid > 0) setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus.seq]);

  const add = () => {
    const next = [...accounts, emptyAccount()];
    onChange(next);
    if (next.length > INLINE_ROW_LIMIT) setModalOpen(true);
  };

  const change = (accountNumber: string, next: Account) =>
    onChange(accounts.map((a) => (a.account_number === accountNumber ? next : a)));

  const remove = (accountNumber: string) =>
    onChange(accounts.filter((a) => a.account_number !== accountNumber));

  const renderRows = (list: Account[]) => (
    <div className="flex flex-col gap-4">
      {list.map((row, index) => (
        <AccountRow
          key={row.account_number}
          value={row}
          accounts={accounts}
          showErrors={showErrors}
          showLabels={index === 0}
          onChange={(next) => change(row.account_number, next)}
          onRemove={() => remove(row.account_number)}
        />
      ))}
    </div>
  );

  const addButton = (
    <ButtonV2
      variant="ghost"
      size="sm"
      disabled={!addEnabled}
      onClick={add}
      className="text-[#3551F3] bg-transparent self-start disabled:opacity-40"
      prefix={<Plus width="16" height="16" />}
    >
      Add Account
    </ButtonV2>
  );

  return (
    <>
      <div ref={rootRef}>
        <SectionAccordion
          title="Accounts"
          count={accounts.length}
          invalidCount={showErrors ? issue.incompleteRows : 0}
          onExpand={() => setModalOpen(true)}
        >
          <div className="flex flex-col gap-4">
            {accounts.length === 0 ? (
              <p className="text-sm text-gray-500">
                Add account(s) where suspected fraud happened, if applicable. Accounts can be added after case creation too.
              </p>
            ) : (
              renderRows(accounts.slice(0, INLINE_ROW_LIMIT))
            )}
            <div className="flex items-center justify-between">
              {addButton}
              <ViewMoreButton
                overflowCount={Math.max(0, accounts.length - INLINE_ROW_LIMIT)}
                singular="account"
                plural="accounts"
                invalidCount={showErrors ? issue.hiddenInvalid : 0}
                onClick={() => setModalOpen(true)}
              />
            </div>
          </div>
        </SectionAccordion>
      </div>

      {modalOpen && (
        <CreateCaseAddAccountsModal
          accounts={accounts}
          onChange={onChange}
          showErrors={showErrors}
          onClose={() => setModalOpen(false)}
          onDone={() => setModalOpen(false)}
          onAdd={add}
          addEnabled={addEnabled}
        />
      )}
    </>
  );
};

export default AccountsSection;
